import {Injectable} from "@angular/core";

import {
    IUserModel,
    IUserModelResponse,
    IStoryState,
    IUserModelDelta,
    QuestionState,
    QuestionStateMap,
    CompletionState, RecordReadingStateMap, WordWallMode
} from "./usermodel.objects";

import {BookSessionType} from "../../constants";


@Injectable()
export class UserModelState {

    private currentUserModel: IUserModel;
    private currentStoryState: IStoryState = {} as IStoryState;
    private currentDelta: IUserModelDelta; // measures different aspects of what changed in most recent update to model

    constructor() {
    }

    private generateQuestionStatesMap(states: QuestionStateMap): { id: string, status: QuestionState }[] {
        return Object.keys(states).map(k => {
            return {id: k, status: states[k]};
        });
    }

    public findThinkAboutQuestionState(id: number): QuestionState {
        return this.currentStoryState.thinkAboutQuestionStates[id];
    }

    public findQuizQuestionState(id: number): QuestionState {
        return this.currentStoryState.quizQuestionStates[id];
    }

    public get learnerName(): string {
        return this.currentUserModel ? this.currentUserModel.learnerName : "";
    }

    public currentStep(): BookSessionType {
        return this.currentStoryState.currentStep ? this.currentStoryState.currentStep : BookSessionType.WORDWALL;
    }

    public get currentSentenceId(): number {
        return Number.isInteger(this.currentStoryState.currentSentenceId) ?
            this.currentStoryState.currentSentenceId : 0;
    }

    public get currentPageId(): number {
        return Number.isInteger(this.currentStoryState.currentPageId) ? this.currentStoryState.currentPageId : 1;
    }

    public get currentQuizQuestionState(): QuestionState {
        return this.currentStoryState.currentQuizQuestionState ?
            this.currentStoryState.currentQuizQuestionState : QuestionState.NOT_ANSWERED;
    }

    public get currentQuizQuestionId(): number {
        return Number.isInteger(this.currentStoryState.currentQuizQuestionId) ?
            this.currentStoryState.currentQuizQuestionId : 0;
    }

    // number of times user has recorded the story
    public get recordCount(): number {
        return Number.isInteger(this.currentStoryState.recordCount) ? this.currentStoryState.recordCount : 0;
    }

    public get quizCount(): number {
        return Number.isInteger(this.currentStoryState.quizCount) ? this.currentStoryState.quizCount : 0;
    }

    public get highestWcpm(): number {
        return Number.isInteger(this.currentStoryState.highestWcpm) ? this.currentStoryState.highestWcpm : 0;
    }

    public get lastQuizId(): number {
        return Number.isInteger(this.currentStoryState.lastQuizId) ? this.currentStoryState.lastQuizId : 0;
    }

    public get recordingBadge(): number {
        return this.currentStoryState.recordingBadge ? this.currentStoryState.recordingBadge : null;
    }

    public get recordingPoint(): number {
        return this.currentStoryState.recordingPoint ? this.currentStoryState.recordingPoint : 0;
    }

    public get canPlayback(): boolean {
        return this.currentStoryState.canPlayback ? this.currentStoryState.canPlayback : false;
    }

    public get thinkAboutQuestionPoint(): number {
        return Number.isInteger(this.currentStoryState.thinkAboutQuestionPoint) ?
            this.currentStoryState.thinkAboutQuestionPoint : 0;
    }

    public get thinkAboutQuestionStates(): { id: string, status: QuestionState }[] {
        let states = this.currentStoryState.thinkAboutQuestionStates || {};
        return this.generateQuestionStatesMap(states);
    }

    public get quizQuestionStates(): { id: string, status: QuestionState }[] {
        let states = this.currentStoryState.quizQuestionStates || {};
        return this.generateQuestionStatesMap(states);
    }

    public get thinkAboutQuestionStatesMap(): {} {
        let states = this.currentStoryState.thinkAboutQuestionStates || [];
        let stateMap = {};
        Object.keys(states).forEach(k => {
            stateMap[k] = states[k];
        });
        return stateMap;
    }

    public get quizPoint(): number {
        return Number.isInteger(this.currentStoryState.quizPoint) ? this.currentStoryState.quizPoint : 0;
    }

    public get storyTotalPoint(): number {
        return Number.isInteger(this.currentStoryState.totalPoint) ? this.currentStoryState.totalPoint : 0;
    }

    public get storyCompletionState(): CompletionState {
        return this.currentStoryState.storyCompletionState ? this.currentStoryState.storyCompletionState : null;
    }

    public get recordReadingStates(): RecordReadingStateMap {
        return this.currentStoryState.recordReadingStates ? this.currentStoryState.recordReadingStates : null;
    }

    public get hasTAQStar(): boolean {
        return this.currentStoryState.hasTAQStar ? this.currentStoryState.hasTAQStar : false;
    }

    public get hasRecordingStar(): boolean {
        return this.currentStoryState.hasRecordingStar ? this.currentStoryState.hasRecordingStar : false;
    }

    // quiz score (good/ok)
    public get hasQuizStar(): boolean {
        return this.currentStoryState.hasQuizStar ? this.currentStoryState.hasQuizStar : false;
    }

    public get storyUniqName(): string {
        return !!this.currentStoryState && !!this.currentStoryState.storyUniqName ? this.currentStoryState.storyUniqName : null;
    }

    public get totalPoint(): number {
        return this.currentUserModel.totalPoint;
    }

    public get wordWallMode(): WordWallMode {
        return this.currentStoryState.wordWallMode;
    }

    public get userModelDelta(): IUserModelDelta {
        return this.currentDelta;
    }

    public get taqCompletionState(): CompletionState {
        return this.currentStoryState.taqCompletionState;
    }

    public get quizCompletionState(): CompletionState {
        return this.currentStoryState.quizCompletionState;
    }

    public get recordCompletionState(): CompletionState {
        return this.currentStoryState.recordCompletionState;
    }

    private generateUserModelDelta(): IUserModelDelta {
        return {
            canPlayback: false,
            currentStep: null,
            justChangedStep: false,
            justCompletedRecording: false,
            justCompletedSelection: false,
            justCompletedRequiredRecordings: false,
            justCompletedQuiz: false,
            justCompletedTAI: false,
            justCompletedSentence: false,
            initializeState: false
        } as IUserModelDelta;
    }

    public updateUserModelState(newModel: IUserModel, initialize: boolean): IUserModelDelta {
        let delta: IUserModelDelta = this.generateUserModelDelta();
        delta.initializeState = initialize;
        let oldModel = this.currentUserModel;

        let sameSelection: boolean = oldModel && oldModel.currentStoryState && newModel && newModel.currentStoryState &&
            (oldModel.currentStoryState.storyUniqName === newModel.currentStoryState.storyUniqName);
        const oldStep: BookSessionType = sameSelection && oldModel.currentStoryState ? oldModel.currentStoryState.currentStep : null;
        const newStep: BookSessionType = newModel.currentStoryState ? newModel.currentStoryState.currentStep : null;
        delta.justChangedStep = (oldStep !== newStep);
        delta.currentStep = newStep;
        if (sameSelection) {
            delta.canPlayback = newModel.currentStoryState.canPlayback;
            delta.justCompletedRecording = (oldModel.currentStoryState.canPlayback === false) &&
                (newModel.currentStoryState.canPlayback === true);

            delta.justCompletedSelection =
                (oldModel.currentStoryState.storyCompletionState === CompletionState.INCOMPLETE) &&
                (newModel.currentStoryState.storyCompletionState === CompletionState.COMPLETED);

            delta.justCompletedTAI =
                (oldModel.currentStoryState.taqCompletionState === CompletionState.INCOMPLETE ||
                    oldModel.currentStoryState.taqCompletionState === CompletionState.NOT_STARTED) &&
                (newModel.currentStoryState.taqCompletionState === CompletionState.COMPLETED);

            delta.justCompletedRequiredRecordings =
                (oldModel.currentStoryState.recordCompletionState === CompletionState.INCOMPLETE ||
                    oldModel.currentStoryState.recordCompletionState === CompletionState.NOT_STARTED) &&
                (newModel.currentStoryState.recordCompletionState === CompletionState.COMPLETED);

            delta.justCompletedQuiz =
                (oldModel.currentStoryState.quizCompletionState === CompletionState.INCOMPLETE ||
                    oldModel.currentStoryState.quizCompletionState === CompletionState.NOT_STARTED) &&
                (newModel.currentStoryState.quizCompletionState === CompletionState.COMPLETED);

            delta.justCompletedSentence =
                (oldModel.currentStoryState.currentSentenceId < newModel.currentStoryState.currentSentenceId);

        }
        this.currentUserModel = newModel;
        this.currentStoryState = this.currentUserModel && this.currentUserModel.currentStoryState || {} as IStoryState;
        this.currentDelta = delta;
        return delta;
    }



}
